import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Mail, MapPin, Send, CheckCircle, Truck, X } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import SEO from '../components/shared/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ADDON_CATALOG = {
  'fleet-expansion': {
    name: 'Fleet Expansion',
    unitPrice: 75,
    unitLabel: 'per additional truck / month',
    icon: Truck,
  },
};

const ContactPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const addonSlug = searchParams.get('addon');
  const trucksParam = parseInt(searchParams.get('trucks') || '0', 10);
  const addon = addonSlug ? ADDON_CATALOG[addonSlug] : null;
  const trucks = Number.isFinite(trucksParam) ? Math.min(Math.max(trucksParam, 0), 10) : 0;

  const prefillMessage = addon
    ? `Requested Add-On: ${addon.name}${trucks > 0 ? ` — ${trucks} additional truck${trucks > 1 ? 's' : ''} (+$${trucks * addon.unitPrice}/mo)` : ''}\n\nI'd like to add this to my compliance plan. Please reach out with next steps.`
    : '';

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    equipment_type: '',
    ready_to_start: '',
    message: prefillMessage,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      await axios.post(`${API}/contact`, {
        ...formData,
        addon: addonSlug || undefined,
        addon_quantity: addon && trucks > 0 ? trucks : undefined,
        addon_monthly_total: addon && trucks > 0 ? trucks * addon.unitPrice : undefined,
      });
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        equipment_type: '',
        ready_to_start: '',
        message: '',
      });
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <SEO 
        title="Contact Us"
        description="Get in touch with Queen B Logistics. Request a callback, ask questions about our dispatch services, or schedule a consultation. Located in Raleigh, NC."
        canonical="https://queenblogistics.com/contact"
        keywords="contact freight dispatch, trucking dispatch contact, Queen B Logistics contact, dispatch@queenblogistics.com"
      />
      {/* Page Header */}
      <section className="page-header">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="limited-badge mb-6">
            Get in Touch
          </div>
          <h1 className="font-['Barlow_Condensed'] text-5xl md:text-6xl lg:text-7xl font-black text-white uppercase">
            Contact<br />
            <span className="text-[#D4AF37]">Us</span>
          </h1>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <div>
              <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-8">
                Reach Out
              </h2>
              
              <div className="space-y-6 mb-12">
                <a 
                  href="mailto:dispatch@queenblogistics.com"
                  className="flex items-center gap-4 p-4 bg-[#121212] border border-white/5 hover:border-[#D4AF37]/30 transition-colors"
                  data-testid="contact-email"
                >
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center">
                    <Mail className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Email</p>
                    <p className="text-white">dispatch@queenblogistics.com</p>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 bg-[#121212] border border-white/5">
                  <div className="w-12 h-12 bg-[#D4AF37]/10 flex items-center justify-center">
                    <MapPin className="w-5 h-5 text-[#D4AF37]" />
                  </div>
                  <div>
                    <p className="text-white/50 text-sm">Headquarters</p>
                    <p className="text-white">3064 Wake Forest Road #1369</p>
                    <p className="text-white/60">Raleigh, NC 27609</p>
                  </div>
                </div>
              </div>

              {/* Map */}
              <div className="aspect-video bg-[#121212] border border-white/10 overflow-hidden">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d207447.34898804693!2d-78.8391145!3d35.7795897!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x89ac5a2f9f51e0f7%3A0x6790b6528a11f0ad!2sRaleigh%2C%20NC!5e0!3m2!1sen!2sus!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0, filter: 'grayscale(100%) invert(90%) contrast(90%)' }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Queen B Logistics Location"
                />
              </div>
            </div>

            {/* Contact Form */}
            <div>
              <h2 className="font-['Barlow_Condensed'] text-3xl font-bold text-white uppercase mb-8">
                Request a Callback
              </h2>

              {/* Requested add-on banner — visible whenever ?addon=slug in URL */}
              {addon && !isSubmitted && (
                <div
                  className="mb-6 border border-[#D4AF37]/50 bg-gradient-to-r from-[#D4AF37]/12 to-[#D4AF37]/5 p-4 flex items-start gap-3"
                  data-testid="contact-addon-banner"
                >
                  <addon.icon className="w-6 h-6 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="text-[#D4AF37] text-xs uppercase tracking-[0.2em] font-bold mb-1">
                      Requested Add-On
                    </div>
                    <div className="text-white font-semibold">
                      {addon.name}
                      {trucks > 0 && (
                        <span className="text-white/60 font-normal">
                          {' '}· {trucks} additional truck{trucks > 1 ? 's' : ''} · +${trucks * addon.unitPrice}/mo
                        </span>
                      )}
                    </div>
                    <div className="text-white/50 text-xs mt-1">
                      Your message has been pre-filled with this request.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setSearchParams({});
                      setFormData({ ...formData, message: '' });
                    }}
                    className="text-white/50 hover:text-[#D4AF37] transition-colors"
                    aria-label="Clear add-on request"
                    data-testid="contact-addon-clear"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {isSubmitted ? (
                <div className="p-8 bg-[#121212] border border-[#D4AF37]/30 text-center" data-testid="contact-success">
                  <CheckCircle className="w-16 h-16 text-[#D4AF37] mx-auto mb-4" />
                  <h3 className="font-['Barlow_Condensed'] text-2xl font-bold text-white mb-2">
                    Message Sent!
                  </h3>
                  <p className="text-white/60">
                    We'll get back to you within 24-48 hours.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-white/70 mb-2 block">
                        Name <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <Input
                        id="name"
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="input-dark w-full"
                        placeholder="Your name"
                        data-testid="contact-name-input"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-white/70 mb-2 block">
                        Email <span className="text-[#D4AF37]">*</span>
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="input-dark w-full"
                        placeholder="your@email.com"
                        data-testid="contact-email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="phone" className="text-white/70 mb-2 block">
                      Phone <span className="text-[#D4AF37]">*</span>
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      required
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="input-dark w-full"
                      placeholder="(555) 555-5555"
                      data-testid="contact-phone-input"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label className="text-white/70 mb-2 block">
                        Equipment Type
                      </Label>
                      <Select
                        value={formData.equipment_type}
                        onValueChange={(value) => setFormData({ ...formData, equipment_type: value })}
                      >
                        <SelectTrigger className="input-dark w-full" data-testid="contact-equipment-select">
                          <SelectValue placeholder="Select type" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="dry-van">Dry Van</SelectItem>
                          <SelectItem value="reefer">Reefer</SelectItem>
                          <SelectItem value="flatbed">Flatbed</SelectItem>
                          <SelectItem value="step-deck">Step Deck</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-white/70 mb-2 block">
                        How soon are you ready to start?
                      </Label>
                      <Select
                        value={formData.ready_to_start}
                        onValueChange={(value) => setFormData({ ...formData, ready_to_start: value })}
                      >
                        <SelectTrigger className="input-dark w-full" data-testid="contact-ready-select">
                          <SelectValue placeholder="Select timeframe" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="immediately">Immediately</SelectItem>
                          <SelectItem value="1-2-weeks">1-2 Weeks</SelectItem>
                          <SelectItem value="1-month">Within a Month</SelectItem>
                          <SelectItem value="just-exploring">Just Exploring</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-white/70 mb-2 block">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      value={formData.message}
                      onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                      className="input-dark w-full min-h-[120px]"
                      placeholder="Tell us about your needs..."
                      data-testid="contact-message-input"
                    />
                  </div>

                  {error && (
                    <p className="text-red-500 text-sm">{error}</p>
                  )}

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="btn-primary w-full flex items-center justify-center gap-2 gold-glow disabled:opacity-50"
                    data-testid="contact-submit-btn"
                  >
                    {isSubmitting ? 'Sending...' : 'Request Callback'}
                    <Send className="w-4 h-4" />
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ContactPage;
